import React, { useState, useMemo, useEffect } from 'react';
import { useNavigation } from '../contexts/NavigationProvider';
import { useAuth } from '../contexts/AuthProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import SparklesIcon from './icons/SparklesIcon';
import LinkIcon from './icons/LinkIcon';
import PlusIcon from './icons/PlusIcon';
import TrashIcon from './icons/TrashIcon';
import { generateCoverLetter } from '../services/apiService';
import type { Band } from '../types';

const ApplicationDetailPage: React.FC = () => {
    const { viewingOpportunity, handleNavigation, navigationOrigin, bands, handleViewOpportunity } = useNavigation();
    const { currentUser } = useAuth();
    const { createNewApplications } = useOpportunities();

    const [coverLetter, setCoverLetter] = useState('');
    const [links, setLinks] = useState<string[]>(['']);
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiError, setAiError] = useState('');
    const [aiKeywords, setAiKeywords] = useState('');
    
    const [applyAsIndividual, setApplyAsIndividual] = useState(true);
    const [applyAsBands, setApplyAsBands] = useState<Band[]>([]);

    // Robust Band Detection: Filter global bands where current user is a listed member
    const myBands = useMemo(() => {
        if (!currentUser) return [];
        return bands.filter(band => 
            band.members.some(m => m.handle === currentUser.handle)
        );
    }, [currentUser, bands]);
    
    const { hasAppliedAsIndividual, appliedBandIds } = useMemo(() => {
        if (!currentUser || !viewingOpportunity || !viewingOpportunity.applications) return { hasAppliedAsIndividual: false, appliedBandIds: new Set() };
        
        const myApps = viewingOpportunity.applications.filter(app => app.applicant.handle === currentUser.handle && app.status !== 'draft');
        
        const hasAppliedAsIndividual = myApps.some(app => app.applyingAs?.individual);
        const appliedBandIds = new Set(myApps.flatMap(app => app.applyingAs?.bands?.map(b => b.id) || []));
        
        return { hasAppliedAsIndividual, appliedBandIds };
    }, [currentUser, viewingOpportunity]);

    useEffect(() => {
        if (hasAppliedAsIndividual) {
            setApplyAsIndividual(false);
        } else {
            setApplyAsIndividual(true);
        }
    }, [hasAppliedAsIndividual]);
    
    if (!viewingOpportunity || !currentUser) return null;

    const isAiAssistantEnabled = currentUser?.subscriptionTier?.includes('pro_plus');

    const handleLinkChange = (index: number, value: string) => {
        const newLinks = [...links];
        newLinks[index] = value;
        setLinks(newLinks);
    };

    const addLink = () => setLinks([...links, '']);
    const removeLink = (index: number) => setLinks(links.filter((_, i) => i !== index));

    const handleGenerate = async () => {
        if (!isAiAssistantEnabled) return;
        setIsGenerating(true);
        setAiError('');
        try {
            const generated = await generateCoverLetter(currentUser, viewingOpportunity, aiKeywords);
            setCoverLetter(generated);
        } catch(err) {
            setAiError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsGenerating(false);
        }
    }
    
    const handleToggleBand = (band: Band) => {
        setApplyAsBands(prev => 
            prev.some(b => b.id === band.id) 
            ? prev.filter(b => b.id !== band.id)
            : [...prev, band]
        );
    };

    const onSubmit = async () => {
        const applyingAs = {
            individual: applyAsIndividual,
            bands: applyAsBands.map(b => ({ id: b.id, name: b.name }))
        };

        if (!applyingAs.individual && applyingAs.bands.length === 0) {
            alert("Please select at least one entity (yourself or a band) to apply as.");
            return;
        }

        try {
            await createNewApplications(viewingOpportunity.id, {
                applyingAs,
                coverLetter: coverLetter.trim(),
                submissionLinks: links.map(l => l.trim()).filter(Boolean),
            });
            handleNavigation('myActivity');
        } catch (e) {
            console.error("Submission failed", e);
        }
    };
    
    const isSubmitDisabled = !coverLetter.trim() || (!applyAsIndividual && applyAsBands.length === 0);

    return (
        <div className="space-y-6 font-urbanist">
            <button onClick={() => handleNavigation(navigationOrigin || 'myActivity')} className="flex items-center space-x-2 text-brand-muted hover:text-white font-semibold uppercase tracking-widest text-xs">
                <ArrowLeftIcon className="h-5 w-5" />
                <span>Back</span>
            </button>
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                <h2 className="text-3xl font-black text-white tracking-tighter uppercase">Apply for</h2>
                <button
                    onClick={() => handleViewOpportunity(viewingOpportunity)}
                    className="text-xl text-brand-orange font-bold hover:underline text-left w-full focus:outline-none uppercase"
                >
                    {viewingOpportunity.title}
                </button>
            </div>

            <div className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                <div className="bg-gray-800/40 p-4 rounded-xl border border-gray-700">
                    <label className="block text-[10px] font-black text-brand-orange mb-3 uppercase tracking-widest">Apply as: Select one or more</label>
                    <div className="space-y-2">
                        <CheckboxOption 
                            label={`${currentUser.name} (Individual Applicant)`}
                            checked={applyAsIndividual}
                            onToggle={() => setApplyAsIndividual(prev => !prev)}
                            disabled={hasAppliedAsIndividual}
                            isApplied={hasAppliedAsIndividual}
                        />
                        {myBands.map(band => (
                            <CheckboxOption
                                key={band.id}
                                label={`${band.name} (Band / Project)`}
                                checked={applyAsBands.some(b => b.id === band.id)}
                                onToggle={() => handleToggleBand(band)}
                                disabled={appliedBandIds.has(band.id)}
                                isApplied={appliedBandIds.has(band.id)}
                            />
                        ))}
                        {myBands.length === 0 && (
                            <p className="text-[10px] text-brand-muted italic px-2">No projects found. You can add projects to your profile in the Network or Profile tabs.</p>
                        )}
                    </div>
                </div>
                 
                <div className={`bg-gray-800/50 rounded-lg p-4 space-y-3 border border-gray-700/50 ${!isAiAssistantEnabled ? 'opacity-60' : ''}`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <SparklesIcon className="h-5 w-5 text-brand-orange" />
                            <h3 className="text-lg font-bold text-white tracking-tight uppercase">AI Assistant</h3>
                        </div>
                        {!isAiAssistantEnabled && (
                            <span className="bg-yellow-500/20 text-yellow-300 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest">PRO+</span>
                        )}
                    </div>
                    <p className="text-sm text-brand-muted">
                        Enter some key points about why you're a good fit, and let AI draft a professional cover letter.
                    </p>
                     {!isAiAssistantEnabled && (
                        <div className="text-center p-2 bg-gray-900/50 rounded-md">
                            <button type="button" onClick={() => handleNavigation('pumpUpTheVolume')} className="text-[10px] font-bold text-brand-orange hover:underline uppercase tracking-widest">
                                Upgrade to Pro+ to unlock AI drafting
                            </button>
                        </div>
                    )}
                    <div>
                        <label className="block text-[10px] font-bold text-brand-muted mb-1 uppercase tracking-widest">Key Points / Highlights</label>
                        <input
                            type="text"
                            value={aiKeywords}
                            onChange={(e) => setAiKeywords(e.target.value)}
                            placeholder="e.g., Extensive touring experience, proficient in jazz and fusion"
                            className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange disabled:cursor-not-allowed font-medium"
                            disabled={isGenerating || !isAiAssistantEnabled}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleGenerate}
                        disabled={isGenerating || !aiKeywords.trim() || !isAiAssistantEnabled}
                        className="flex items-center justify-center w-full bg-brand-orange/20 text-brand-orange border border-brand-orange font-bold py-3 px-4 rounded-full hover:bg-brand-orange/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all uppercase tracking-widest text-xs"
                    >
                        <SparklesIcon className="h-5 w-5 mr-2" />
                        {isGenerating ? 'Drafting...' : 'Draft Cover Letter'}
                    </button>
                    {aiError && <p className="text-xs text-red-400 mt-2 text-center font-bold">{aiError}</p>}
                </div>

                <div>
                    <label className="block text-[10px] font-bold text-brand-muted mb-1 uppercase tracking-widest">Your Message / Cover Letter</label>
                    <textarea
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                        placeholder="Introduce yourself or your band and explain why you're a great fit."
                        className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange resize-none font-bold leading-relaxed"
                        rows={8}
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-bold text-brand-muted mb-1 uppercase tracking-widest">Submission Links (Demos, Videos, Web)</label>
                    <div className="space-y-2">
                    {links.map((link, index) => (
                        <div key={index} className="flex items-center space-x-2">
                            <LinkIcon className="h-5 w-5 text-brand-muted"/>
                            <input
                                type="url"
                                value={link}
                                onChange={(e) => handleLinkChange(index, e.target.value)}
                                placeholder="https://youtube.com/..."
                                className="w-full bg-gray-700 text-white rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-brand-orange font-bold text-sm"
                            />
                            <button onClick={() => removeLink(index)} className="p-1 text-red-400 hover:text-red-300">
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        </div>
                    ))}
                    </div>
                    <button onClick={addLink} className="mt-2 flex items-center space-x-1 text-xs text-brand-orange hover:underline font-black uppercase tracking-widest">
                        <PlusIcon className="h-4 w-4" />
                        <span>Add another link</span>
                    </button>
                </div>
                <div className="flex justify-end items-center pt-4">
                    <button 
                        onClick={onSubmit}
                        disabled={isSubmitDisabled}
                        className="w-full sm:w-auto bg-brand-orange text-white font-black px-12 py-4 rounded-full hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-brand-orange/20 uppercase tracking-widest text-sm"
                    >
                        Submit Application
                    </button>
                </div>
            </div>
        </div>
    );
};

const CheckboxOption: React.FC<{ label: string, checked: boolean, onToggle: () => void, disabled: boolean, isApplied?: boolean }> = ({ label, checked, onToggle, disabled, isApplied }) => (
    <label className={`flex items-center p-4 rounded-xl border-2 transition-all ${disabled && !isApplied ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-brand-orange/50'} ${checked ? 'bg-brand-orange/10 border-brand-orange' : 'bg-gray-800/40 border-gray-700'}`}>
        <input 
            type="checkbox" 
            checked={checked}
            onChange={onToggle}
            disabled={disabled}
            className="h-5 w-5 rounded bg-gray-700 border-gray-600 text-brand-orange focus:ring-brand-orange focus:ring-2 disabled:cursor-not-allowed"
        />
        <span className="ml-3 text-sm font-bold text-white uppercase tracking-tight">{label}</span>
        {isApplied && <span className="ml-auto text-[10px] font-black text-green-400 bg-green-500/10 px-2 py-1 rounded-full uppercase tracking-widest border border-green-500/20">Already Applied</span>}
    </label>
);

export default ApplicationDetailPage;